import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import Plus from "../../assets/images/pl.svg";
import Search from "../../assets/images/search.svg";
import Spinner from "../../Components/loader";
import Pagination from "../../Components/Pagination";
import axiosInstance from "../../helpers/axiosInstance";
import "./productGroup.css";
import {
    assignProductsToSubGroup,
    changeDateFormat,
    changeProductGroupStatus,
    clearProductGroupMsg,
    createProductGroup,
    deleteProductGroup,
    getBulkProducts,
    getProductGroups,
    updateProductGroup
} from "../../actions";

const role = localStorage.getItem("userRole");
const PAGE_SIZE = 10;

const ProductGroup = (props) => {
    const { list, page, loading, success_msg, bulkProducts, dispatch } = props;

    const [searchKey, setSearchKey] = useState("");
    const [pageNo, setPageNo] = useState(0);
    const [statusFilter, setStatusFilter] = useState("all"); // all | active | inactive

    const [show, setShow] = useState(false);
    const [editing, setEditing] = useState(null);
    const [groupName, setGroupName] = useState("");
    const [nameError, setNameError] = useState("");
    // rows the user is editing: existing sub groups carry productSubGroupId,
    // newly added ones do not — the API creates those
    const [subGroups, setSubGroups] = useState([]);
    const [subGroupError, setSubGroupError] = useState("");
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [confirmToggle, setConfirmToggle] = useState(null);

    // assign-products-to-subgroup modal
    const [assignTarget, setAssignTarget] = useState(null); // the sub group being assigned to
    const [assignSearch, setAssignSearch] = useState("");
    const [assignSelected, setAssignSelected] = useState([]);
    const [assignExcludeIds, setAssignExcludeIds] = useState([]); // products already in the sub group

    const openAssign = (sg) => {
        setAssignTarget(sg);
        setAssignSearch("");
        setAssignSelected([]);
        setAssignExcludeIds([]);
        dispatch(getBulkProducts("", "STATIC"));
        // products already mapped to this sub group are hidden from the picker
        axiosInstance
            .get(`staff/productSubGroups/${sg.productSubGroupId}/productIds`)
            .then((res) => setAssignExcludeIds(res?.data?.data || []))
            .catch(() => setAssignExcludeIds([]));
    };

    const onAssignSearch = (value) => {
        setAssignSearch(value);
        dispatch(getBulkProducts(value, "STATIC"));
    };

    const toggleAssignProduct = (productId) => {
        setAssignSelected((prev) =>
            prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId]
        );
    };

    const submitAssign = () => {
        if (assignSelected.length === 0) {
            toast("Select at least one product");
            return;
        }
        dispatch(assignProductsToSubGroup({
            subGroupId: assignTarget.productSubGroupId,
            productIds: assignSelected
        }));
    };

    const statusParam =
        statusFilter === "active" ? true : statusFilter === "inactive" ? false : "all";

    const fetchList = () => {
        dispatch(
            getProductGroups({
                searchKey,
                pageNo,
                sizeOfPage: PAGE_SIZE,
                status: statusParam
            })
        );
    };

    useEffect(() => {
        fetchList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchKey, pageNo, statusFilter]);

    useEffect(() => {
        if (success_msg) {
            toast(success_msg.data);
            if (success_msg.status === 200) {
                setShow(false);
                setConfirmDelete(null);
                setAssignTarget(null);
                fetchList();
            }
            dispatch(clearProductGroupMsg());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [success_msg]);

    const openAdd = () => {
        setEditing(null);
        setGroupName("");
        setNameError("");
        setSubGroups([]);
        setSubGroupError("");
        setShow(true);
    };

    const openEdit = (item) => {
        setEditing(item);
        setGroupName(item.groupName || "");
        setNameError("");
        setSubGroups(
            (item.subGroups || []).map((sg) => ({
                productSubGroupId: sg.productSubGroupId,
                subGroupName: sg.subGroupName || "",
                active: sg.active
            }))
        );
        setSubGroupError("");
        setShow(true);
    };

    const addSubGroupRow = () => {
        setSubGroups([...subGroups, { subGroupName: "", active: true }]);
        setSubGroupError("");
    };

    const changeSubGroupName = (index, value) => {
        const next = subGroups.map((sg, i) => (i === index ? { ...sg, subGroupName: value } : sg));
        setSubGroups(next);
        setSubGroupError("");
    };

    const removeSubGroupRow = (index) => {
        setSubGroups(subGroups.filter((sg, i) => i !== index));
        setSubGroupError("");
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!groupName || groupName.trim().length === 0) {
            setNameError("Group name is required");
            return;
        }
        const rows = subGroups
            .map((sg) => ({ ...sg, subGroupName: (sg.subGroupName || "").trim() }))
            .filter((sg) => sg.subGroupName.length > 0);
        const seen = [];
        for (const row of rows) {
            const key = row.subGroupName.toLowerCase();
            if (seen.indexOf(key) !== -1) {
                setSubGroupError(`Duplicate sub group name: ${row.subGroupName}`);
                return;
            }
            seen.push(key);
        }
        if (editing) {
            dispatch(
                updateProductGroup({
                    productGroupId: editing.productGroupId,
                    groupName: groupName.trim(),
                    subGroups: rows
                })
            );
        } else {
            dispatch(createProductGroup({ groupName: groupName.trim(), subGroups: rows }));
        }
    };

    const toggleStatus = () => {
        if (confirmToggle) {
            dispatch(
                changeProductGroupStatus({
                    productGroupId: confirmToggle.productGroupId,
                    active: !confirmToggle.active
                })
            );
            setConfirmToggle(null);
        }
    };

    const handleDelete = () => {
        if (confirmDelete) {
            dispatch(deleteProductGroup(confirmDelete.productGroupId));
        }
    };

    const onSearch = (e) => {
        setPageNo(0);
        setSearchKey(e.target.value);
    };

    const onFilter = (value) => {
        setPageNo(0);
        setStatusFilter(value);
    };

    const paginationData = {
        totalElements: page?.totalElements,
        totalPages: page?.totalPages,
        offset: page?.pageable?.offset,
        numberOfElements: page?.numberOfElements,
        num: (page?.number ?? 0) + 1
    };

    return (
        <div className="product-group-tab">
            <div className="staff-page card">
                <div className="pg-header">
                    <div className="pg-status-filter">
                        <button
                            className={statusFilter === "all" ? "pg-filter active" : "pg-filter"}
                            onClick={() => onFilter("all")}
                        >
                            All
                        </button>
                        <button
                            className={statusFilter === "active" ? "pg-filter active" : "pg-filter"}
                            onClick={() => onFilter("active")}
                        >
                            Active
                        </button>
                        <button
                            className={statusFilter === "inactive" ? "pg-filter active" : "pg-filter"}
                            onClick={() => onFilter("inactive")}
                        >
                            Inactive
                        </button>
                    </div>
                    <div className="pg-header-right">
                        <div className="customer-search">
                            <span>
                                <img src={Search} alt=".." className="search-img" />
                            </span>
                            <input
                                type="search"
                                placeholder="Search"
                                onChange={onSearch}
                                className="main-search form-control"
                            />
                        </div>
                        <button className="add-product create-staff pg-add-btn" onClick={openAdd}>
                            <img src={Plus} alt="" /> Add Group
                        </button>
                    </div>
                </div>

                <div className="table-class">
                    <table className="table table-borderless">
                        <thead>
                            <tr>
                                <th scope="col">Group Name</th>
                                <th scope="col">Sub Groups</th>
                                <th scope="col">Created</th>
                                <th scope="col" style={{ textAlign: "center" }}>Status</th>
                                <th scope="col">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {list && list.length > 0 ? (
                                list.map((item, i) => (
                                    <tr key={item.productGroupId ?? i}>
                                        <td>
                                            {item.groupName}
                                            <span className="pg-count" title="Active products in this group">
                                                {item.productCount ?? 0} product{item.productCount === 1 ? "" : "s"}
                                            </span>
                                        </td>
                                        <td>
                                            {item.subGroups && item.subGroups.length > 0 ? (
                                                <span className="pg-subgroup-chips">
                                                    {item.subGroups.map((sg) => (
                                                        <span
                                                            key={sg.productSubGroupId}
                                                            className={sg.active ? "pg-chip" : "pg-chip inactive"}
                                                            title={`${sg.productCount ?? 0} active product(s)${sg.active ? "" : " — inactive sub group"}`}
                                                        >
                                                            {sg.subGroupName}
                                                            <b className="pg-chip-count">{sg.productCount ?? 0}</b>
                                                            {sg.active && (
                                                                <b
                                                                    className="pg-chip-add"
                                                                    title="Assign products to this sub group"
                                                                    onClick={() => openAssign(sg)}
                                                                    style={{ marginLeft: "6px", cursor: "pointer", fontWeight: 700 }}
                                                                >
                                                                    +
                                                                </b>
                                                            )}
                                                        </span>
                                                    ))}
                                                </span>
                                            ) : (
                                                "-"
                                            )}
                                        </td>
                                        <td>{item.createdAt ? changeDateFormat(item.createdAt) : "-"}</td>
                                        <td style={{ textAlign: "center" }}>
                                            <button
                                                className={item.active ? "download_btn_active" : "download_btn_inactive"}
                                                onClick={() => setConfirmToggle(item)}
                                                title="Click to change status"
                                            >
                                                {item.active ? "Active" : "Inactive"}
                                            </button>
                                        </td>
                                        <td>
                                            <button className="Edit-staff" onClick={() => openEdit(item)}>
                                                Edit
                                            </button>
                                            {role === "admin" && !item.active && (
                                                <button
                                                    className="Edit-staff"
                                                    style={{ marginLeft: "8px", color: "#d9534f" }}
                                                    onClick={() => setConfirmDelete(item)}
                                                >
                                                    Delete
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : !loading ? (
                                <tr>
                                    <td colSpan="5">
                                        <h1 className="empty-data">No Records Found</h1>
                                    </td>
                                </tr>
                            ) : (
                                <tr>
                                    <td colSpan="5">
                                        <Spinner />
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            {page && page.totalPages > 1 ? (
                <Pagination data={paginationData} onChange={(selected) => setPageNo(selected)} />
            ) : null}

            {/* Add / Edit modal */}
            <Modal show={show} onHide={() => setShow(false)} centered>
                <div className="modal-staff">
                    <div className="head">
                        <div>
                            <i className="fas fa-arrow-left" onClick={() => setShow(false)}></i>
                            <h4>{editing ? "Edit" : "Add"} Product Group</h4>
                        </div>
                    </div>
                    <form className="floating-label-form pg-modal-form" onSubmit={handleSubmit} style={{ padding: "20px" }}>
                        <label className="form-control-placeholder-pack">Group Name <span>*</span></label>
                        <input
                            className="form-control"
                            value={groupName}
                            maxLength={100}
                            onChange={(e) => {
                                setGroupName(e.target.value);
                                if (e.target.value.trim().length > 0) setNameError("");
                            }}
                        />
                        {nameError ? <p style={{ color: "#d9534f" }}>{nameError}</p> : null}

                        <div className="pg-subgroup-block">
                            <div className="pg-subgroup-head">
                                <label className="form-control-placeholder-pack">Sub Groups</label>
                                <button type="button" className="pg-subgroup-add" onClick={addSubGroupRow}>
                                    + Add Sub Group
                                </button>
                            </div>
                            {subGroups.length === 0 ? (
                                <p className="pg-subgroup-empty">
                                    No sub groups yet. Products are mapped to sub groups, so add at least one
                                    to make this group selectable on a product.
                                </p>
                            ) : (
                                // a group can carry 30+ sub groups: the list scrolls in place
                                // so the modal keeps its size and the submit button stays reachable
                                <div className="pg-subgroup-list">
                                    {subGroups.map((sg, i) => (
                                        <div className="pg-subgroup-row" key={sg.productSubGroupId ?? `new-${i}`}>
                                            <input
                                                className="form-control"
                                                value={sg.subGroupName}
                                                maxLength={100}
                                                placeholder="Sub group name"
                                                onChange={(e) => changeSubGroupName(i, e.target.value)}
                                            />
                                            <button
                                                type="button"
                                                className="pg-subgroup-remove"
                                                title="Remove this sub group"
                                                onClick={() => removeSubGroupRow(i)}
                                            >
                                                &times;
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {subGroupError ? <p style={{ color: "#d9534f" }}>{subGroupError}</p> : null}
                        </div>

                        <button type="submit" className="sub-btn" style={{ marginTop: "16px" }}>
                            {editing ? "Update" : "Create"} Group
                        </button>
                    </form>
                </div>
            </Modal>

            {/* Delete confirm modal */}
            <Modal show={!!confirmDelete} onHide={() => setConfirmDelete(null)} centered>
                <div className="pg-modal">
                    <h4>Delete Product Group</h4>
                    <p>
                        Are you sure you want to permanently delete
                        {" "}<b>{confirmDelete?.groupName}</b>? This cannot be undone.
                    </p>
                    <div className="pg-modal-actions">
                        <button className="pg-btn pg-btn-danger" onClick={handleDelete}>Delete</button>
                        <button className="pg-btn pg-btn-secondary" onClick={() => setConfirmDelete(null)}>Cancel</button>
                    </div>
                </div>
            </Modal>

            {/* Activate / Inactivate confirm modal */}
            <Modal show={!!confirmToggle} onHide={() => setConfirmToggle(null)} centered>
                <div className="pg-modal">
                    <h4>{confirmToggle?.active ? "Mark Inactive" : "Mark Active"}</h4>
                    <p>
                        Are you sure you want to mark
                        {" "}<b>{confirmToggle?.groupName}</b>{" "}
                        as <b>{confirmToggle?.active ? "Inactive" : "Active"}</b>?
                    </p>
                    <div className="pg-modal-actions">
                        <button className="pg-btn pg-btn-primary" onClick={toggleStatus}>Yes</button>
                        <button className="pg-btn pg-btn-secondary" onClick={() => setConfirmToggle(null)}>No</button>
                    </div>
                </div>
            </Modal>

            {/* Assign multiple products to a sub group */}
            <Modal show={!!assignTarget} onHide={() => setAssignTarget(null)} centered>
                <div className="pg-modal">
                    <h4>Assign Products</h4>
                    <p>
                        Add products to <b>{assignTarget?.subGroupName}</b>
                    </p>
                    <div className="customer-search" style={{ marginBottom: "12px" }}>
                        <span><img src={Search} alt=".." className="search-img" /></span>
                        <input
                            type="search"
                            placeholder="Search products"
                            className="main-search"
                            value={assignSearch}
                            onChange={(e) => onAssignSearch(e.target.value)}
                        />
                    </div>
                    <div style={{ maxHeight: "320px", overflowY: "auto" }}>
                        {(() => {
                            const available = (bulkProducts?.content || []).filter(
                                (item) => !assignExcludeIds.includes(item.productId)
                            );
                            return available.length > 0 ? (
                                available.map((item) => (
                                <label
                                    key={item.productId}
                                    style={{ display: "flex", alignItems: "center", gap: "8px", padding: "6px 4px", cursor: "pointer" }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={assignSelected.includes(item.productId)}
                                        onChange={() => toggleAssignProduct(item.productId)}
                                    />
                                    <span>
                                        <b>{item.articleId}</b> — {item.productName}
                                    </span>
                                </label>
                                ))
                            ) : (
                                <p className="empty-data">No products found</p>
                            );
                        })()}
                    </div>
                    <div className="pg-modal-actions" style={{ marginTop: "12px" }}>
                        <button className="pg-btn pg-btn-primary" onClick={submitAssign}>
                            Assign{assignSelected.length > 0 ? ` (${assignSelected.length})` : ""}
                        </button>
                        <button className="pg-btn pg-btn-secondary" onClick={() => setAssignTarget(null)}>Cancel</button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

const mapStateToProps = (state) => {
    const { productGroup } = state;
    return {
        list: productGroup.list,
        page: productGroup.page,
        loading: productGroup.loading,
        success_msg: productGroup.success_msg,
        bulkProducts: state.product.bulkProducts
    };
};

export default connect(mapStateToProps)(ProductGroup);
